/**
 * Screen 13 — per-organisation API keys, for a client's TMS or ERP.
 *
 * The secret is shown **once**, at creation, and stored only as a hash. The
 * reveal panel therefore has to be unmissable and has to stay on screen until
 * the user acknowledges it: closing the dialog is the moment the value stops
 * existing anywhere it can be recovered from. So focus goes to the secret when
 * it appears, and any other way of closing the dialog while it shows asks first.
 *
 * The default scope set is `deca:read deca:write` — deliberately without
 * `deca:void` and without any admin scope, so a leaked integration key cannot
 * void a legal document or mint another key.
 */

import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { unwrap, type ApiKey, type Scope } from '@deca/api'

import { api } from '../app/api.ts'
import { apiErrorMessage } from '../app/errors.ts'
import { qk, useApiKeys } from '../app/queries.ts'
import { useAuth } from '../app/auth.tsx'
import { useOrganisationChoice } from '../app/useOrganisationChoice.ts'
import { abilitiesFor } from '../deca/roles.ts'
import { formatDateTime } from '../deca/format.ts'
import { AdminShell } from './AdminShell.tsx'
import { Button, Card, Chip, CopyButton, NoAccessState, Notice } from '../ui/primitives.tsx'
import { DataTable, type Column } from '../ui/DataTable.tsx'
import { Dialog } from '../ui/Dialog.tsx'
import { TextInput } from '../ui/form.tsx'
import { useToast } from '../ui/Toast.tsx'

/**
 * The scopes a key can hold. `org:read`, `org:admin` and `apikey:admin` are not among them: the
 * server refuses them on every key (`403 SCOPE_NOT_ISSUABLE` — administration needs a person's
 * session), so offering them only ever produced an error.
 */
const ISSUABLE_SCOPES: Scope[] = ['deca:read', 'deca:write', 'deca:modify', 'deca:void', 'import:write']

const DEFAULT_SCOPES: Scope[] = ['deca:read', 'deca:write']

export function AdminApiKeysScreen() {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const client = useQueryClient()
  const { principal } = useAuth()
  const abilities = abilitiesFor(principal)
  const { organisation: context } = useOrganisationChoice()

  const list = useApiKeys(abilities.administer)
  const [createOpen, setCreateOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!abilities.administer) {
    return (
      <AdminShell>
        <Card>
          <NoAccessState />
        </Card>
      </AdminShell>
    )
  }

  async function revoke(key: ApiKey) {
    if (!window.confirm(t('admin.apiKeys.revokeConfirm', { name: key.name }))) return
    setError(null)
    try {
      await unwrap(api.DELETE('/api/v1/api-keys/{keyId}', { params: { path: { keyId: key.id } } }))
      void client.invalidateQueries({ queryKey: qk.apiKeys() })
      toast.success(t('admin.apiKeys.revoked'))
    } catch (caught) {
      setError(apiErrorMessage(caught, t))
    }
  }

  const columns: Column<ApiKey>[] = [
    {
      key: 'name',
      header: t('admin.apiKeys.name'),
      render: (row) => (
        <div className="stack-sm" style={{ gap: 0 }}>
          <span style={{ fontWeight: 600 }}>{row.name}</span>
          <span className="mono muted">{row.prefix}</span>
        </div>
      ),
    },
    {
      key: 'scopes',
      header: t('admin.apiKeys.scopes'),
      render: (row) => (
        <div className="row wrap" style={{ gap: 'var(--space-xs)' }}>
          {row.scopes.map((scope) => (
            <span key={scope} className="role-badge">
              {t(`scope.${scope}`)}
            </span>
          ))}
        </div>
      ),
    },
    {
      key: 'lastUsed',
      header: t('admin.apiKeys.lastUsed'),
      numeric: true,
      render: (row) =>
        row.lastUsedAt ? formatDateTime(row.lastUsedAt, i18n.language) : t('common.never'),
    },
    {
      key: 'status',
      header: t('admin.apiKeys.status', { defaultValue: t('admin.users.status') }),
      // At the end of a phone card's heading (#111): a revoked key reads as one before anything else.
      card: 'end',
      render: (row) =>
        row.revokedAt ? (
          <Chip tone="danger" icon="ban" muted>
            {t('admin.apiKeys.revokedAt')}
          </Chip>
        ) : (
          <Chip tone="success" icon="checkCircle">
            {t('admin.users.statusACTIVE')}
          </Chip>
        ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'right',
      render: (row) =>
        row.revokedAt ? null : (
          <div className="table__actions">
            {/* Named for its key: the visible word is the same on every row, and a list of buttons that all say
                «Revocar» cannot be told apart by someone who hears them one at a time. */}
            <Button
              size="sm"
              variant="ghost"
              icon="ban"
              aria-label={t('admin.apiKeys.revokeNamed', { name: row.name })}
              onClick={() => void revoke(row)}
            >
              {t('admin.apiKeys.revoke')}
            </Button>
          </div>
        ),
    },
  ]

  return (
    <AdminShell
      heading={t('admin.apiKeys.titleScoped', {
        // Whose keys these are, not who is signed in — the list already follows the chosen
        // company through `X-Organisation-Id`, and a heading that disagreed with it would be
        // read as the keys belonging to ASGTRANS.
        organisation: context?.name ?? principal?.organisation.legalName ?? '',
      })}
      subtitle={t('admin.apiKeys.subtitle')}
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}

      <Card
        flush
        actions={
          <Button variant="primary" icon="plus" onClick={() => setCreateOpen(true)}>
            {t('admin.apiKeys.create')}
          </Button>
        }
      >
        <DataTable
          rows={list.data?.data}
          columns={columns}
          rowKey={(row) => row.id}
          caption={t('admin.apiKeys.title')}
          isLoading={list.isLoading}
          error={list.error}
          onRetry={() => void list.refetch()}
        />
      </Card>

      <CreateApiKeyDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => void client.invalidateQueries({ queryKey: qk.apiKeys() })}
      />
    </AdminShell>
  )
}

function CreateApiKeyDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: () => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [scopes, setScopes] = useState<Scope[]>(DEFAULT_SCOPES)
  const [secret, setSecret] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const reveal = useRef<HTMLDivElement>(null)

  // The field and «Crear» that had focus are replaced by the secret in the same render, and focus would fall to
  // the page behind the dialog: Tab would leave the modal, Enter on a sidebar link would navigate away and lose
  // the secret, and a screen reader would say nothing about a secret that is shown once. Move it to the secret.
  useEffect(() => {
    if (secret) reveal.current?.focus()
  }, [secret])

  function close() {
    setName('')
    setScopes(DEFAULT_SCOPES)
    setSecret(null)
    setError(null)
    onClose()
  }

  /**
   * ✕, Escape and a click on the backdrop. While the secret is on screen each would throw it away unread, from one
   * stray click, so they ask first. «Lo he copiado» is the user saying it has been copied and closes directly.
   */
  function dismiss() {
    if (secret && !window.confirm(t('admin.apiKeys.secretDiscardConfirm'))) return
    close()
  }

  async function submit() {
    if (!name.trim()) {
      setError(t('errors.required'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      const created = await unwrap(
        api.POST('/api/v1/api-keys', { body: { name: name.trim(), scopes } }),
      )
      setSecret(created.secret)
      onCreated()
    } catch (caught) {
      setError(apiErrorMessage(caught, t))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      busy={busy}
      title={secret ? t('admin.apiKeys.secretTitle') : t('admin.apiKeys.create')}
      onClose={dismiss}
      footer={
        secret ? (
          <Button variant="primary" icon="check" onClick={close}>
            {t('admin.apiKeys.secretAcknowledge')}
          </Button>
        ) : (
          <>
            <Button onClick={close} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" loading={busy} disabled={busy} onClick={() => void submit()}>
              {t('common.create')}
            </Button>
          </>
        )
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}

      {secret ? (
        // `stack-lg` is the dialog body's own gap, so the two keep the spacing they had as its direct children.
        <div className="stack-lg" ref={reveal} tabIndex={-1}>
          <Notice tone="warning" title={t('admin.apiKeys.secretTitle')}>
            {t('admin.apiKeys.secretHelp')}
          </Notice>
          <div className="secret-reveal">
            <code>{secret}</code>
            <CopyButton value={secret} />
          </div>
        </div>
      ) : (
        <>
          <TextInput
            label={t('admin.apiKeys.name')}
            placeholder={t('admin.apiKeys.namePlaceholder')}
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
            // The dialog has no `<form>`, so Enter creates only through this, and never while a create is on its way.
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !busy) void submit()
            }}
          />

          <div className="stack-sm">
            <span className="field__label">{t('admin.apiKeys.scopes')}</span>
            <p className="small muted">{t('admin.apiKeys.defaultScopesHelp')}</p>
            <div className="stack-sm">
              {ISSUABLE_SCOPES.map((scope) => (
                <label key={scope} className="checkbox">
                  <input
                    type="checkbox"
                    checked={scopes.includes(scope)}
                    onChange={(event) => {
                      // Read now, not in the updater: React may run the updater after the event has
                      // been dispatched, when `currentTarget` is already null — the second of two
                      // quick clicks crashed the portal that way.
                      const checked = event.currentTarget.checked
                      setScopes((current) =>
                        checked ? [...current, scope] : current.filter((value) => value !== scope),
                      )
                    }}
                  />
                  <span>
                    {t(`scope.${scope}`)} <code className="mono muted">{scope}</code>
                  </span>
                </label>
              ))}
            </div>
          </div>
        </>
      )}
    </Dialog>
  )
}
